const RECONNECT_MIN_MS = 800;
const RECONNECT_MAX_MS = 15000;
const PING_MS = 25000;
const FALLBACK_POLL_MS = 45000;

let socket = null;
let token = null;
let wantedTopics = new Set();
let handlers = new Map();
let statusHandlers = new Set();
let reconnectTimer = null;
let pingTimer = null;
let reconnectAttempt = 0;
let manualClose = false;
let connected = false;

function wsUrl() {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const q = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${proto}//${window.location.host}/api/ws${q}`;
}

function emitStatus() {
  for (const fn of statusHandlers) {
    try { fn(connected); } catch {}
  }
}

function clearTimers() {
  if (reconnectTimer) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (pingTimer) {
    window.clearInterval(pingTimer);
    pingTimer = null;
  }
}

function scheduleReconnect() {
  if (manualClose || reconnectTimer) return;
  const delay = Math.min(
    RECONNECT_MAX_MS,
    RECONNECT_MIN_MS * (2 ** Math.min(reconnectAttempt, 5)) + Math.floor(Math.random() * 400),
  );
  reconnectAttempt += 1;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

function dispatch(topic, data) {
  const set = handlers.get(topic);
  if (!set) return;
  for (const fn of set) {
    try { fn(data); } catch {}
  }
}

function send(obj) {
  if (!socket || socket.readyState !== WebSocket.OPEN) return false;
  try {
    socket.send(JSON.stringify(obj));
    return true;
  } catch {
    return false;
  }
}

function flushSubscriptions() {
  if (!wantedTopics.size) return;
  send({ type: 'subscribe', topics: [...wantedTopics] });
}

function connect() {
  if (typeof window === 'undefined') return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;

  manualClose = false;
  clearTimers();
  let next;
  try {
    next = new WebSocket(wsUrl());
  } catch {
    scheduleReconnect();
    return;
  }
  socket = next;

  next.onopen = () => {
    connected = true;
    reconnectAttempt = 0;
    emitStatus();
    if (token) send({ type: 'auth', token });
    flushSubscriptions();
    pingTimer = window.setInterval(() => send({ type: 'ping' }), PING_MS);
  };

  next.onmessage = (ev) => {
    let msg;
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return;
    }
    if (!msg || typeof msg !== 'object') return;
    if (msg.type === 'event' && msg.topic) {
      dispatch(msg.topic, msg.data);
    }
  };

  next.onclose = () => {
    connected = false;
    emitStatus();
    clearTimers();
    socket = null;
    if (!manualClose) scheduleReconnect();
  };

  next.onerror = () => {
    try { next.close(); } catch {}
  };
}

export function setRealtimeAuthToken(nextToken) {
  const value = nextToken || null;
  if (token === value) return;
  token = value;
  if (socket && socket.readyState === WebSocket.OPEN) {
    if (token) send({ type: 'auth', token });
    flushSubscriptions();
    return;
  }
  if (wantedTopics.size || token) connect();
}

export function ensureRealtimeConnected() {
  if (!socket || socket.readyState === WebSocket.CLOSED) connect();
}

export function isRealtimeConnected() {
  return connected;
}

export function subscribeRealtimeStatus(fn) {
  statusHandlers.add(fn);
  try { fn(connected); } catch {}
  return () => statusHandlers.delete(fn);
}

export function subscribeRealtimeTopic(topic, handler) {
  if (!topic || typeof handler !== 'function') return () => {};
  let set = handlers.get(topic);
  if (!set) {
    set = new Set();
    handlers.set(topic, set);
  }
  set.add(handler);
  wantedTopics.add(topic);
  ensureRealtimeConnected();
  if (connected) send({ type: 'subscribe', topics: [topic] });

  return () => {
    const cur = handlers.get(topic);
    if (cur) {
      cur.delete(handler);
      if (!cur.size) {
        handlers.delete(topic);
        wantedTopics.delete(topic);
        send({ type: 'unsubscribe', topics: [topic] });
      }
    }
  };
}

export function getRealtimeFallbackPollMs() {
  return connected ? FALLBACK_POLL_MS : null;
}

try {
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      if (!connected) connect();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !connected) connect();
    });
    window.addEventListener('pageshow', () => {
      if (!connected) connect();
    });
  }
} catch {
}
